import { Box, Card, CardActionArea, CardProps, keyframes, Stack, styled } from "@mui/material";
import GridImage from "@Resources/game/UIGridBackground.png";

export type ColorGradient = "blue" | "green" | "orange" | "purple" | "red";

export const NavCardActionArea = styled(CardActionArea)(({ theme }) => ({
  position: "relative",
  display: "flex",
  padding: theme.spacing(4),
  minHeight: theme.spacing(27.5),
  justifyContent: "start",
  alignItems: "start",
}));

export const TopRight = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(4),
  right: theme.spacing(4),
}));

export const BottomRight = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
}));

export const NavCardMediumActionArea = styled(CardActionArea)<{ imageUrl?: string }>(({ theme, imageUrl }) => ({
  position: "relative",
  display: "flex",
  alignItems: "end",
  justifyContent: "center",
  padding: theme.spacing(4, 2.5),
  backgroundImage: `url(${imageUrl})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  width: "calc(100% - 10px)",
  height: "calc(100% - 10px)",
  borderRadius: "40px",
  cornerShape: "squircle",
}));

interface INavCardIconWrapper {
  iconbgcolor?: ColorGradient;
}

export const NavCardIconWrapper = styled(Stack)<INavCardIconWrapper>(({ theme, iconbgcolor }) => ({
  background: iconbgcolor ? theme.palette.dsPrimaryGradient[iconbgcolor] : "rgba(0, 0, 0, 0.16)",
  "--gradient-direction": "270deg",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
}));

export const GridImageCard = styled((props: CardProps) => <Card elevation={0} {...props} />)<{ disabled?: boolean }>(({ theme, disabled }) => ({
  position: "relative",
  backgroundColor: theme.palette.dsSurface.v100,
  backgroundImage: "none",
  opacity: disabled ? 0.7 : 1,

  ...(!disabled && {
    "&::before": {
      position: `absolute`,
      inset: 0,
      content: '" "',
      backgroundImage: `url(${GridImage});`,
      backgroundSize: "480px 409px",
      backgroundPositionY: "bottom",
      filter: theme.palette.mode === "dark" ? "brightness(0%) saturate(100%) invert(5%) sepia(5%) hue-rotate(187deg) contrast(93%);" : undefined,
    },
  }),

  // Render the Card content over the grid image
  "& > *": {
    position: "relative",
  },
}));

export const BottomGradient = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "100%",
  height: theme.spacing(25),
  bottom: -1,
  background: "linear-gradient(0deg, #121316 0%, rgba(18, 19, 22, 0.00) 100%)",
  borderRadius: "20px",
}));

export const NavCardBlur = styled(Box)(({}) => ({
  position: "absolute",
  inset: 0,
  backdropFilter: "grayscale(100%)",
  background: "rgba(18, 19, 22, 0.20)",
  borderRadius: "20px",
  zIndex: 1,
}));

export const CardLeftGradient = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  width: "70%",
  height: "100%",
  background: "linear-gradient(90deg, #121316 0%, rgba(18, 19, 22, 0.00) 100%)",
}));

/**
 * Border glow animation source: https://codepen.io/liyrofx/pen/poVZeEG
 */

interface Hoverable {
  r: string;
  g: string;
  b: string;
  isHovered?: boolean;
}

const rotate = keyframes`
  100% {
    transform: translate(-50%, -50%) rotate(1turn);
  }
`;

const glowBorderBase: any = {
  height: "100%",
  width: "100%",
  position: "absolute",
  overflow: "hidden",
  zIndex: "0",
  borderRadius: "10px",
  boxSizing: "border-box",
};

const glowBorderBefore: any = {
  content: '""',
  zIndex: "-2",
  textAlign: "center",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%) rotate(0deg)",
  position: "absolute",
  width: "99999px",
  height: "99999px",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "0 0",
  /* change speed here */
  animation: `${rotate} 5s linear infinite`,
};

export const GlowOnHoverCard = styled(Box)<Hoverable>(({ isHovered, r, g, b }) => ({
  ...glowBorderBase,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "::before": {
    opacity: isHovered ? 1 : 0,
    transition: "opacity 0.5s cubic-bezier(0.18, 0.9, 0.58, 1)",
    ...glowBorderBefore,
    /*border color*/
    backgroundImage: `conic-gradient(from 180deg, transparent 0%, rgba(${r}, ${g}, ${b}, 0.6) 20%, rgb(${r}, ${g}, ${b}) 25%, rgba(${r}, ${g}, ${b}, 0.5) 30%, transparent 48%, rgba(${r}, ${g}, ${b}, 0.5) 68%, rgb(${r}, ${g}, ${b}) 75%, rgba(${r}, ${g}, ${b}, 0.6) 82%, transparent 100%)`,
  },
  "::after": {
    content: '""',
    position: "absolute",
    zIndex: "-1",
    /* border width */
    left: "5px",
    top: "5px",
    /* double the px from the border width left */
    width: "calc(100% - 10px)",
    height: "calc(100% - 10px)",
    borderRadius: "20px",
  },
}));

export const BorderGlow = styled(Box)<Hoverable>(({ isHovered, r, g, b }) => ({
  ...glowBorderBase,
  filter: "blur(20px)",
  // If hover, apply glow border
  "::before": {
    opacity: isHovered ? 1 : 0,
    transition: "opacity 0.5s cubic-bezier(0.18, 0.9, 0.58, 1)",
    ...glowBorderBefore,
    /*border color*/
    backgroundImage: `conic-gradient(from 180deg, transparent 0%, rgba(${r}, ${g}, ${b}, 0.6) 20%, rgb(${r}, ${g}, ${b}) 25%, rgba(${r}, ${g}, ${b}, 0.5) 30%, transparent 48%, rgba(${r}, ${g}, ${b}, 0.5) 68%, rgb(${r}, ${g}, ${b}) 75%, rgba(${r}, ${g}, ${b}, 0.6) 82%, transparent 100%)`,
  },
}));
